/*
 *   Copyright (c) 2021 RCUBE PLANET PVT LTD
 *   All rights reserved.
 */

import React, { createContext, Reducer, useEffect, useReducer } from "react";
import { useCookies } from "react-cookie";
import { history } from "../history";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const checkIsAuthenticated = async (token: string, path: string) => {
  return await fetch(`${process.env.REACT_APP_API_DOMAIN}${path}`, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: "Bearer " + token,
    },
    redirect: "follow",
    referrerPolicy: "no-referrer",
    body: JSON.stringify({}),
  });
};
const authLogin = async (url: string, credentials: {}) => {
  return await fetch(`${process.env.REACT_APP_API_DOMAIN}${url}`, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    redirect: "follow",
    referrerPolicy: "no-referrer",
    body: JSON.stringify({
      ...credentials,
    }),
  });
};

const authLogout = async (url: string, token: string) => {
  return await fetch(`${process.env.REACT_APP_API_DOMAIN}${url}`, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: "Bearer " + token,
    },
    redirect: "follow",
    referrerPolicy: "no-referrer",
    body: JSON.stringify({}),
  });
};
interface propType {}
interface stateType {
  isLoading: boolean;
  user: userType | null;
  unreadNotification: number;
}

interface AuthContextInterface {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (param: any) => any;
  logout: () => void;
  user: userType | null;
  unreadNotification: number;
  checkAuth: () => void;
  http: <bodyType extends {}, responseType extends {}>(
    path: string,
    body: bodyType,
    opts?: RequestInit
  ) => Promise<{
    json: responseType;
  }>;
}
type ReducerAction =
  | { type: "setIsLoading"; payload: boolean }
  | { type: "setUser"; payload: userType }
  | { type: "removeUser"; payload?: undefined }
  | { type: "setUnreadNotification"; payload: number };

const initialState: stateType = {
  isLoading: true,
  user: null,
  unreadNotification: 0,
};

const AuthReducer = (state: stateType, action: ReducerAction): stateType => {
  switch (action.type) {
    case "setIsLoading":
      return {
        ...state,
        isLoading: action.payload,
      };
    case "setUser":
      return { ...state, user: action.payload };
    case "removeUser":
      return { ...state, user: null };

    default:
      return state;
  }
};

export const AuthContext = createContext<AuthContextInterface>(null as any);
export const Auth: React.FC<propType> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<stateType, ReducerAction>>(
    AuthReducer,
    initialState
  );
  const [cookies, setCookie, removeCookie] = useCookies(["user_type", "token"]);
  const { isLoading, user, unreadNotification } = state;

  useEffect(() => {
    // alert("check is hitting");
    valueProvider.checkAuth();
  }, [cookies.token]);

  const valueProvider: AuthContextInterface = {
    checkAuth: () => {
      dispatch({ type: "setIsLoading", payload: true });
      console.log("token is ", cookies.token);
      checkIsAuthenticated(cookies.token, "/check")
        .then(async (response) => {
          const jsonResult =
            (await response.json()) as httpResponses["check"]["response"];
          if (jsonResult?.code !== 200) {
            if (
              jsonResult?.error === "E_UNAUTHORIZED_ACCESS: ACCOUNT_INACTIVE"
            ) {
              alert("Your account is currently under review.");
            }
            throw jsonResult?.errorTrace?.[0]?.message || jsonResult.error;
          }

          dispatch({ type: "setUser", payload: jsonResult.staff! });
          dispatch({ type: "setIsLoading", payload: false });
          dispatch({
            type: "setUnreadNotification",
            payload: jsonResult.unreadNotifications,
          });
        })
        .catch((err) => {
          if (typeof err !== "string") {
            err = "Network error";
          }
          console.warn(err || "Something went wrong!");

          dispatch({ type: "setIsLoading", payload: false });
          dispatch({ type: "removeUser" });
        });
    },
    login: (credentials: {}) => {
      dispatch({ type: "setIsLoading", payload: true });
      authLogin("/login", credentials)
        .then(async (response) => {
          const jsonResult = await response.json();
          console.log("jsonResult", jsonResult);

          if (jsonResult?.code !== 200) {
            throw jsonResult?.errorTrace?.[0]?.message || jsonResult?.error;
          }

          dispatch({ type: "setIsLoading", payload: false });
          setCookie("token", jsonResult?.token.token, { path: "/" });
          history.push("/");
        })
        .catch((err) => {
          if (typeof err !== "string") {
            err = "Network error";
          }
          (toast as any).warning("Invalid email/password");

          dispatch({ type: "setIsLoading", payload: false });
          removeCookie("token");
          removeCookie("user_type");
          dispatch({ type: "removeUser" });
        });
    },
    logout: () => {
      dispatch({ type: "setIsLoading", payload: true });
      authLogout("/logout", cookies.token)
        .then(async (response) => {
          const jsonResult = await response.json();
          console.log("jsonResult", jsonResult);

          if (jsonResult?.code !== 200) {
            throw jsonResult?.errorTrace?.[0]?.message || jsonResult?.error;
          }
          console.log("Logout successful!");
        })
        .catch((err) => {
          if (typeof err !== "string") {
            err = "Network error";
          }
          console.log(err || "Something went wrong!");
        })
        .finally(() => {
          dispatch({ type: "setIsLoading", payload: false });
          removeCookie("token");
          dispatch({ type: "removeUser" });
          history.push("/login");
        });
    },
    async http(path, body, opts = {}) {
      const url = path.includes("://")
        ? path
        : `${process.env.REACT_APP_API_DOMAIN}${path}`;
      const response = await fetch(url, {
        ...opts,
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
          Authorization: "Bearer " + cookies.token,
        },

        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.code === 500 && data.internalCode === "E_INVALID_ID_API_TOKEN") {
        removeCookie("token");
        removeCookie("user_type");
        history.push("/login");
        throw new Error("Invalid Token");
      }

      return { json: data };
    },
    isAuthenticated: !!state.user,

    isLoading,
    user,
    unreadNotification,
  };

  return (
    <>
      <AuthContext.Provider value={valueProvider}>
        <ToastContainer />
        {children}
      </AuthContext.Provider>
    </>
  );
};

interface httpResponses {
  check: {
    response: {
      code: 200 | 500;
      error?: string;
      errorTrace?: any;
      staff?: userType;
      unreadNotifications: number;
    };
  };
}

type userType = {
  id: number;
  email: string;
  mobile: string;
  name: string;
  role: string;
  remember_me_token: string;
  is_active: number;
  pin: number;
  created_at: string;
  updated_at: string;
};
